'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useBasePath } from '../navigation/base-path';
import { InfiniteScrollTrigger } from './infinite-scroll-trigger';
import { S3Image } from './s3-image';
import { VideoThumbnail } from './video-thumbnail';
import { cn } from '../utils/cn';

export type ProfileHistoryFilter = 'photo' | 'video' | 'music' | 'chat';

const historyFilters: { id: ProfileHistoryFilter; label: string }[] = [
  { id: 'photo', label: 'Фото' },
  { id: 'video', label: 'Видео' },
  { id: 'music', label: 'Музыка' },
  { id: 'chat', label: 'Чат' },
];

export interface ProfileHistoryItem {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  resultUrl: string | null;
  thumbnailUrl: string | null;
  resultType: 'IMAGE' | 'VIDEO' | 'AUDIO';
  prompt: string | null;
  resultWidth?: number | null;
  resultHeight?: number | null;
  feedStatus?: 'NOT_SUBMITTED' | 'PENDING_MODERATION' | 'APPROVED' | 'REJECTED';
}

interface ProfileHistoryGridProps {
  history?: ProfileHistoryItem[];
  initialFilter?: ProfileHistoryFilter | null;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore: () => void;
}

export function ProfileHistoryGrid({
  history,
  initialFilter,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: ProfileHistoryGridProps) {
  const [filter, setFilter] = useState<ProfileHistoryFilter>(
    initialFilter === 'video' ? 'video' : initialFilter === 'music' ? 'music' : 'photo',
  );

  const filteredHistory = history?.filter((item) => {
    if (filter === 'photo') {
      return item.resultType === 'IMAGE' || (!item.resultType && item.status !== 'COMPLETED');
    }
    if (filter === 'video') return item.resultType === 'VIDEO';
    if (filter === 'music') return item.resultType === 'AUDIO';
    return false;
  });

  return (
    <div>
      <div className="mx-4 mb-3">
        <div className="flex rounded-[20px] bg-[#25272a] p-[3px]">
          {historyFilters.map((item) => (
            <button
              key={item.id}
              onClick={() => setFilter(item.id)}
              className={cn(
                'flex-1 rounded-[18px] py-2 text-xs font-semibold transition-all',
                filter === item.id ? 'bg-[#fafafa] text-[#0f0f0f]' : 'text-[#fafafa]',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {filter === 'chat' && (
        <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
          <p className="text-[#8e8e93]">Чаты скоро будут доступны</p>
        </div>
      )}

      {(filter === 'photo' || filter === 'video' || filter === 'music') &&
      (!filteredHistory || filteredHistory.length === 0) ? (
        <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
          <p className="text-[#8e8e93]">
            {filter === 'photo'
              ? 'Нет сгенерированных фото'
              : filter === 'video'
                ? 'Нет сгенерированных видео'
                : 'Нет сгенерированной музыки'}
          </p>
        </div>
      ) : (filter === 'photo' || filter === 'video' || filter === 'music') && filteredHistory ? (
        <>
          <ProfileHistoryMasonry items={filteredHistory} />
          <InfiniteScrollTrigger
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
          />
        </>
      ) : null}
    </div>
  );
}

function ProfileHistoryMasonry({ items }: { items: ProfileHistoryItem[] }) {
  const [left, right] = balanceMasonryColumns(items);
  const basePath = useBasePath();

  const renderItem = (item: ProfileHistoryItem) => {
    const isCompleted = item.resultUrl && item.status === 'COMPLETED';
    const cardStyle =
      isCompleted && item.resultType === 'IMAGE'
        ? undefined
        : item.resultWidth && item.resultHeight
          ? { aspectRatio: `${item.resultWidth}/${item.resultHeight}` }
          : { minHeight: 120 };

    return (
      <Link
        key={item.id}
        href={`${basePath}/history/${item.id}?source=profile`}
        className="relative block overflow-hidden bg-card"
        style={cardStyle}
      >
        {isCompleted ? (
          item.resultType === 'VIDEO' ? (
            <VideoThumbnail
              src={item.resultUrl!}
              poster={item.thumbnailUrl}
              alt={item.prompt || 'Video'}
              iconSize="lg"
            />
          ) : item.resultType === 'AUDIO' ? (
            <AudioProfileThumb thumbnailUrl={item.thumbnailUrl} prompt={item.prompt} />
          ) : (
            <S3Image
              src={item.thumbnailUrl || item.resultUrl!}
              alt={item.prompt || ''}
              width={item.resultWidth || 390}
              height={item.resultHeight || 390}
              className="block h-auto w-full"
            />
          )
        ) : item.status === 'FAILED' ? (
          <div className="relative aspect-square w-full bg-[#4e4e50]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets/error-icon.svg"
              alt=""
              className="absolute left-1/2 top-[13%] w-[35%] -translate-x-1/2"
            />
            <p className="absolute bottom-[16%] left-0 right-0 px-3 text-center font-sans text-xs leading-[1.75] tracking-tight text-white">
              Что-то пошло не так
            </p>
            <p className="absolute bottom-[6%] left-0 right-0 text-center font-sans text-xs font-semibold text-[#ef6444]">
              Повторить
            </p>
          </div>
        ) : (
          <div className="relative aspect-square w-full bg-[#25272a]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets/logo-full-gray.svg"
              alt=""
              className="absolute left-1/2 top-[22%] w-[36%] -translate-x-1/2"
            />
            <div className="absolute left-1/2 top-[76%] flex -translate-x-1/2 gap-[7px]">
              <span
                className="h-[7px] w-[7px] animate-bounce-dot rounded-full bg-accent"
                style={{ animationDelay: '0ms' }}
              />
              <span
                className="h-[7px] w-[7px] animate-bounce-dot rounded-full bg-accent"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="h-[7px] w-[7px] animate-bounce-dot rounded-full bg-accent"
                style={{ animationDelay: '300ms' }}
              />
            </div>
          </div>
        )}

        {item.feedStatus === 'APPROVED' && (
          <span
            className="absolute right-0 top-0 flex items-center justify-center rounded-bl-[10px] bg-[#9dd94a]"
            style={{ padding: '2px 2px 4px 5px', height: 21 }}
          >
            <svg width="14" height="14" viewBox="0 0 21 21" fill="none" className="shrink-0" style={{ width: 14, height: 14 }}>
              <circle cx="10.5" cy="10.5" r="9" stroke="#fff" strokeWidth="1.4" />
              <path d="M6.44 10.46l2.72 2.72 5.44-5.44" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
        {item.feedStatus === 'PENDING_MODERATION' && (
          <span className="absolute right-0 top-0 z-10 rounded-bl-[10px] bg-[#25272a] px-2 pb-1 pt-0.5 text-[11px] font-medium text-white">
            На модерации
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">{left.map(renderItem)}</div>
      <div className="flex flex-1 flex-col gap-1">{right.map(renderItem)}</div>
    </div>
  );
}

function AudioProfileThumb({
  thumbnailUrl,
  prompt,
}: {
  thumbnailUrl?: string | null;
  prompt?: string | null;
}) {
  return (
    <div className="relative flex aspect-square w-full flex-col items-center justify-center overflow-hidden bg-[#25272a]">
      {thumbnailUrl ? (
        <S3Image
          src={thumbnailUrl}
          alt={prompt || 'Music cover'}
          width={390}
          height={390}
          className="absolute inset-0 h-full w-full object-cover opacity-75"
        />
      ) : null}
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative z-[1] flex h-14 w-14 items-center justify-center rounded-full bg-white/15 backdrop-blur">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      </div>
      {prompt ? (
        <p className="relative z-[1] mt-3 line-clamp-2 max-w-[80%] text-center text-xs font-medium text-white">
          {prompt}
        </p>
      ) : null}
    </div>
  );
}

function balanceMasonryColumns<T extends { resultWidth?: number | null; resultHeight?: number | null }>(
  items: T[],
): [T[], T[]] {
  const left: T[] = [];
  const right: T[] = [];
  let leftHeight = 0;
  let rightHeight = 0;

  for (const item of items) {
    const ratio =
      item.resultWidth && item.resultHeight
        ? item.resultHeight / item.resultWidth
        : 1;

    if (leftHeight <= rightHeight) {
      left.push(item);
      leftHeight += ratio;
    } else {
      right.push(item);
      rightHeight += ratio;
    }
  }

  return [left, right];
}
